import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  Link,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ApiError } from '@/auth/api';
import {
  emptyRegistration,
  passwordConfirmationError,
  validateRegistration,
  type RegistrationFieldErrors,
} from '@/auth/registration';
import type { RegisterCustomerFormValues } from '@/auth/types';
import { useAuth } from '@/auth/useAuth';
import {
  createQuotationRequest,
  getDeclaredFields,
  getPhoneModels,
  uploadQuotationPhoto,
} from '@/features/operations/api';
import {
  clearQuotationDraft,
  loadQuotationDraft,
  saveQuotationDraft,
} from '@/features/operations/quotationDraft';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import type {
  DeclaredAnswerInput,
  PublicDeclaredField,
  PublicEstimate,
  PhoneModelOption,
} from '@/features/operations/types';
import { NarrowPage } from '@/components/NarrowPage';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';

const stepLabels = ['Tu equipo', 'Estado declarado', 'Tu cuenta'];

type AccountMode = 'register' | 'login';

export function QuotationRequestPage() {
  const { formatMoney } = usePriceDisplay();
  const { isAuthenticated, isBootstrapping, login, logout, registerCustomer, token, user } =
    useAuth();
  /// Quoting needs a customer account: the estimate is only shown once signed in, and the
  /// contact is the account's (ADR 0008). Without one, the last step signs in or registers and
  /// sends the request in the same action, so the details are typed once.
  const isCustomer = isAuthenticated && user?.role === 'CUSTOMER';
  /// What was filled before, kept across a reload and the activation link (U-1, #107).
  const [restoredDraft] = useState(() => loadQuotationDraft());
  const [step, setStep] = useState(restoredDraft?.step ?? 0);
  const [accountMode, setAccountMode] = useState<AccountMode>('register');
  const [registration, setRegistration] = useState<RegisterCustomerFormValues>(emptyRegistration);
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [confirmationError, setConfirmationError] = useState<string | undefined>();
  const [loginValues, setLoginValues] = useState({ email: '', password: '' });
  const [accountErrors, setAccountErrors] = useState<RegistrationFieldErrors>({});
  /// Set when the email claimed an account PhoneTec created at the counter: it is activated from
  /// the mail, and the form comes back with what was filled.
  const [activationSentTo, setActivationSentTo] = useState<string | null>(null);
  /// The photos cannot be kept across a reload or another tab: a draft that lost them says so.
  const [photosLost, setPhotosLost] = useState(
    Boolean(restoredDraft && restoredDraft.photoCount > restoredDraft.photos.length),
  );
  const [storageGb, setStorageGb] = useState(restoredDraft?.storageGb ?? '');
  const [phoneModels, setPhoneModels] = useState<PhoneModelOption[]>([]);
  const [selectedBrand, setSelectedBrand] = useState(restoredDraft?.selectedBrand ?? '');
  const [selectedModelId, setSelectedModelId] = useState(restoredDraft?.selectedModelId ?? '');
  const [isCatalogueLoading, setIsCatalogueLoading] = useState(true);
  // Set when the catalogue could not be fetched, which is different from a catalogue that
  // loaded and is empty: one is worth retrying, the other is not.
  const [catalogueFailed, setCatalogueFailed] = useState(false);
  /// Which field's "how do I check this?" note is open, by id. Used to be a boolean for
  /// battery health, back when battery health was the only field that had one.
  const [openHelpFieldId, setOpenHelpFieldId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<File[]>(restoredDraft?.photos ?? []);
  const [photoWarning, setPhotoWarning] = useState<string | null>(null);
  /// The questions PhoneTec configured, and what was answered to each. Neither this page nor
  /// the backend holds the list: both read the same rows.
  const [declaredFields, setDeclaredFields] = useState<PublicDeclaredField[]>([]);
  /// The answers are sent against the configured questions, so the request cannot go out before
  /// they load: a form restored after signing in opens on the last step and could otherwise be
  /// sent with its answers dropped.
  const [areFieldsLoaded, setAreFieldsLoaded] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>(restoredDraft?.answers ?? {});
  const [declaredCondition, setDeclaredCondition] = useState(
    restoredDraft?.declaredCondition ?? '',
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submittedReference, setSubmittedReference] = useState<string | null>(null);
  const [submittedEstimate, setSubmittedEstimate] = useState<PublicEstimate | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * A request always names a catalogue entry, so without the catalogue there is no form.
   *
   * This replaced a free-text fallback: the device a request creates now points at a
   * registered brand, and letting an unauthenticated form invent brand names would put rows
   * into the catalogue PhoneTec administers. The cost is that a phone PhoneTec has not
   * loaded cannot be submitted at all, which is a product decision validated with them and
   * recorded in ADR 0002.
   */
  const loadPhoneModels = async () => {
    setIsCatalogueLoading(true);
    setCatalogueFailed(false);

    try {
      setPhoneModels(await getPhoneModels());
    } catch {
      setPhoneModels([]);
      setCatalogueFailed(true);
    } finally {
      setIsCatalogueLoading(false);
    }
  };

  /**
   * The questions themselves. A failure here is treated like a catalogue failure and not like
   * an empty configuration: an empty list is a PhoneTec that asks nothing, which is valid.
   */
  const loadDeclaredFields = async () => {
    try {
      setDeclaredFields(await getDeclaredFields());
      setAreFieldsLoaded(true);
    } catch {
      setDeclaredFields([]);
      setCatalogueFailed(true);
    }
  };

  useEffect(() => {
    void loadPhoneModels();
    void loadDeclaredFields();
  }, []);

  const catalogueBrands = useMemo(
    () => Array.from(new Set(phoneModels.map((item) => item.brand))),
    [phoneModels],
  );

  /**
   * A catalogue with a single active brand has nothing to ask: the control would be a step
   * whose only possible answer is already known. The brand still reaches the request — the
   * backend copies it from the catalogue row that `phoneModelId` names — it just stops being
   * a question.
   *
   * Derived rather than written into `selectedBrand` by an effect. The catalogue can be
   * replaced under the form, because Reintentar reloads it, and `startAnotherRequest` clears
   * the selection: state kept in step with the catalogue is state that can fall out of step
   * with it.
   */
  const onlyBrand = catalogueBrands.length === 1 ? catalogueBrands[0] : null;
  const brandName = onlyBrand ?? selectedBrand;

  const modelsForBrand = useMemo(
    () => phoneModels.filter((item) => item.brand === brandName),
    [phoneModels, brandName],
  );

  const selectedModel = useMemo(
    () => phoneModels.find((item) => item.id === selectedModelId) || null,
    [phoneModels, selectedModelId],
  );

  /**
   * Convenience only, and generic over whatever PhoneTec configured. The backend validates the
   * same things against the same rows, because a form is not what makes an answer valid
   * (BR-063).
   */
  const describeAnswerProblem = (): string | null => {
    const missing = declaredFields.filter(
      (field) => field.isRequired && !(answers[field.id] || '').trim(),
    );

    if (missing.length) {
      return `Completá ${missing.map((field) => field.label.toLowerCase()).join(', ')} para continuar.`;
    }

    const outOfRange = declaredFields.find((field) => {
      if (field.type !== 'PERCENTAGE') {
        return false;
      }

      const raw = (answers[field.id] || '').trim();

      if (!raw) {
        return false;
      }

      const value = Number(raw);
      return !Number.isInteger(value) || value < 0 || value > 100;
    });

    if (outOfRange) {
      return `${outOfRange.label} tiene que ser un número entero entre 0 y 100.`;
    }

    return null;
  };

  const buildAnswers = (): DeclaredAnswerInput[] =>
    declaredFields
      .filter((field) => (answers[field.id] || '').trim())
      .map((field) =>
        field.type === 'SINGLE_CHOICE'
          ? { fieldId: field.id, optionId: answers[field.id] }
          : { fieldId: field.id, percentValue: Number(answers[field.id]) },
      );

  const advance = () => {
    setErrorMessage(null);
    if (step === 0) {
      const hasValidCapacity = Boolean(storageGb) && Number(storageGb) > 0;

      if (!selectedModelId || !hasValidCapacity) {
        // Naming a control that is not on screen would send the applicant looking for it.
        setErrorMessage(
          onlyBrand
            ? 'Elegí modelo y capacidad para continuar.'
            : 'Elegí marca, modelo y capacidad para continuar.',
        );
        return;
      }
    }
    if (step === 1) {
      const problem = describeAnswerProblem();

      if (problem) {
        setErrorMessage(problem);
        return;
      }
    }
    setStep((current) => Math.min(current + 1, stepLabels.length - 1));
  };

  const MAX_PHOTOS = 5;
  const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
  const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  // Convenience only. The backend sniffs the bytes and enforces the caps, because nothing
  // arriving from a browser is trustworthy (BR-063).
  const handlePhotoSelection = (fileList: FileList | null) => {
    if (!fileList) {
      return;
    }

    const accepted: File[] = [];
    const rejected: string[] = [];

    for (const file of Array.from(fileList)) {
      if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
        rejected.push(`${file.name}: formato no aceptado`);
      } else if (file.size > MAX_PHOTO_BYTES) {
        rejected.push(`${file.name}: supera los 5 MB`);
      } else {
        accepted.push(file);
      }
    }

    const room = MAX_PHOTOS - photos.length;

    if (accepted.length > room) {
      rejected.push(`Solo se pueden adjuntar ${MAX_PHOTOS} fotos.`);
    }

    setPhotos((current) => [...current, ...accepted.slice(0, Math.max(0, room))]);
    setPhotoWarning(rejected.length ? rejected.join(' · ') : null);
  };

  // Kept while the form is filled, so a reload or the activation link does not lose it. Not
  // once it was sent: the confirmation is the end of this request.
  useEffect(() => {
    if (submittedReference) {
      return;
    }

    if (!selectedModelId && step === 0) {
      return;
    }

    saveQuotationDraft({
      step,
      selectedBrand,
      selectedModelId,
      storageGb,
      answers,
      declaredCondition,
      photoCount: photos.length,
      photos,
    });
  }, [
    answers,
    declaredCondition,
    photos,
    selectedBrand,
    selectedModelId,
    step,
    storageGb,
    submittedReference,
  ]);

  const startAnotherRequest = () => {
    clearQuotationDraft();
    setSubmittedReference(null);
    setPhotos([]);
    setPhotosLost(false);
    setPhotoWarning(null);
    setSubmittedEstimate(null);
    setErrorMessage(null);
    setStorageGb('');
    setSelectedBrand('');
    setSelectedModelId('');
    setAnswers({});
    setDeclaredCondition('');
    setStep(0);
  };

  /**
   * Signs in or registers with what the last step asked for, and answers with the session to
   * quote with. Null when the request must not be sent yet: a field was wrong, the credentials
   * were rejected, or the email claimed an account created at the counter, which is activated
   * from the mail first (ADR 0008).
   */
  const signInFromForm = async (): Promise<string | null> => {
    if (accountMode === 'login') {
      const errors: RegistrationFieldErrors = {};
      if (!loginValues.email.trim()) errors.email = 'Ingresá tu email.';
      if (!loginValues.password) errors.password = 'Ingresá tu contraseña.';
      setAccountErrors(errors);
      if (Object.keys(errors).length) return null;

      try {
        const session = await login(loginValues);
        // The login is the same for staff (#271). A panel account cannot quote: the request is
        // not sent, and the page shows why with the session it now has.
        return session.user.role === 'CUSTOMER' ? session.accessToken : null;
      } catch (error) {
        if (error instanceof ApiError && error.status === 429) {
          setErrorMessage(
            'Alcanzaste el límite de intentos. Esperá unos minutos y volvé a intentar.',
          );
        } else if (error instanceof ApiError && (error.status === 400 || error.status === 401)) {
          setErrorMessage(
            'Email o contraseña incorrectos. Si PhoneTec te creó la cuenta en el local, activala desde el mail que te enviamos.',
          );
        } else {
          setErrorMessage('No pudimos iniciar sesión. Volvé a intentar en unos minutos.');
        }
        return null;
      }
    }

    const errors = validateRegistration(registration);
    const nextConfirmationError = passwordConfirmationError(
      registration.password,
      passwordConfirmation,
    );
    setAccountErrors(errors);
    setConfirmationError(nextConfirmationError);
    if (Object.keys(errors).length || nextConfirmationError) return null;

    try {
      const result = await registerCustomer(registration);

      if (!result.session) {
        setActivationSentTo(result.email);
        return null;
      }

      return result.session.accessToken;
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        setErrorMessage(
          'Alcanzaste el límite de intentos. Esperá unos minutos y volvé a intentar.',
        );
      } else if (error instanceof ApiError && error.status === 409) {
        setAccountMode('login');
        setLoginValues({ email: registration.email, password: '' });
        setErrorMessage('Ya existe una cuenta con ese email. Ingresá con tu contraseña.');
      } else if (error instanceof ApiError && error.status === 400) {
        setErrorMessage('Revisá tus datos y volvé a intentar.');
      } else {
        setErrorMessage('No pudimos crear tu cuenta. Volvé a intentar en unos minutos.');
      }
      return null;
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    if (isAuthenticated && !isCustomer) {
      return;
    }
    const problem = describeAnswerProblem();

    if (problem) {
      setErrorMessage(problem);
      return;
    }
    setIsSubmitting(true);
    try {
      const sessionToken = isCustomer ? token : await signInFromForm();

      if (!sessionToken) {
        return;
      }

      const response = await createQuotationRequest(sessionToken, {
        // Brand and model are deliberately not sent: the backend reads them from the
        // catalogue entry, so sending them could only contradict it. Neither is the contact:
        // it is the account's.
        phoneModelId: selectedModelId,
        storageGb: Number(storageGb),
        answers: buildAnswers(),
        declaredCondition: declaredCondition || undefined,
      });
      // The form is replaced by a confirmation rather than reset. Resetting left the
      // applicant looking at an empty first step with the confirmation scrolled off the top,
      // which reads as though the submission was lost.
      // Uploaded after the request exists, one at a time. A photo that fails does not undo
      // the submission: the intake is what matters and the photos are supporting evidence.
      const failedPhotos: string[] = [];

      for (const photo of photos) {
        try {
          await uploadQuotationPhoto(response.quotation.id, response.quotation.uploadToken, photo);
        } catch {
          failedPhotos.push(photo.name);
        }
      }

      clearQuotationDraft();
      setPhotoWarning(
        failedPhotos.length
          ? `No pudimos subir ${failedPhotos.join(', ')}. Tu solicitud quedó registrada igual y PhoneTec te va a contactar.`
          : null,
      );
      setSubmittedReference(response.quotation.id);
      setSubmittedEstimate(response.quotation.estimate ?? { available: false });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        // The session expired while the form was open. What was filled stays on screen.
        logout();
        setErrorMessage('Tu sesión venció. Ingresá de nuevo para ver tu estimación.');
        setAccountMode('login');
      } else {
        setErrorMessage('No pudimos enviar tu solicitud. Volvé a intentar en unos minutos.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <NarrowPage maxWidth={560} spacing={3}>
      <Box textAlign="center">
        <Typography variant="overline" color="primary.main" fontWeight={800} letterSpacing=".12em">
          VENDÉ TU CELULAR
        </Typography>
        <Typography component="h1" variant="h2" mt={0.5}>
          Cotizá tu celular en pocos pasos.
        </Typography>
      </Box>
      {/* Visible on every step and after enviar, so the disclaimer holds both before and
          after the request is submitted (BR-010, BR-011, BR-012). */}
      <Alert severity="info" variant="outlined">
        Esta cotización es estimada y no vinculante. PhoneTec confirma el valor final luego de la
        revisión técnica presencial del equipo, que puede diferir de lo declarado acá.
      </Alert>
      {submittedReference ? null : (
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="overline" color="text.secondary">
              PASO {step + 1} DE 3 · {stepLabels[step]}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Math.round(((step + 1) / 3) * 100)}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={((step + 1) / 3) * 100}
            sx={{
              height: 6,
              borderRadius: 99,
              bgcolor: '#EDF0F3',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'primary.main',
                transition: 'transform 320ms ease-out',
              },
            }}
          />
        </Stack>
      )}
      {submittedReference ? (
        <SectionCard title="¡Recibimos tu solicitud!">
          <Stack spacing={2.5}>
            <Alert severity="success">
              Referencia: {submittedReference}. Guardala para consultarnos por esta solicitud.
            </Alert>
            {photoWarning ? <Alert severity="warning">{photoWarning}</Alert> : null}
            {submittedEstimate?.available ? (
              <Box>
                <Typography variant="overline" color="text.secondary">
                  ESTIMACIÓN PRELIMINAR
                </Typography>
                <Typography variant="h3" color="primary.main">
                  {formatMoney(submittedEstimate.amountMinor, submittedEstimate.currency)}
                </Typography>
              </Box>
            ) : (
              // No number is shown rather than a zero or a placeholder: an amount we cannot
              // compute is one PhoneTec never set, and inventing one is exactly what the
              // whole design avoids.
              <Typography color="text.secondary">
                Todavía no podemos estimar un valor online para este equipo. PhoneTec revisa la
                solicitud y te contacta para coordinar la revisión.
              </Typography>
            )}
            <Typography color="text.secondary">
              El valor sigue siendo estimado: PhoneTec lo confirma recién después de la revisión
              técnica presencial del equipo. Te vamos a contactar para coordinarla.
            </Typography>
            <Box>
              <PrimaryButton type="button" onClick={startAnotherRequest}>
                Cotizar otro equipo
              </PrimaryButton>
            </Box>
          </Stack>
        </SectionCard>
      ) : (
        <SectionCard
          title={
            step === 0
              ? '¿Qué celular querés cotizar?'
              : step === 1
                ? '¿Cómo está tu celular?'
                : isCustomer
                  ? 'Todo listo para ver tu estimación'
                  : 'Tu cuenta para ver la estimación'
          }
        >
          <Stack component="form" spacing={2.5} noValidate onSubmit={handleSubmit}>
            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
            {photosLost ? (
              <Alert severity="info" onClose={() => setPhotosLost(false)}>
                Recuperamos lo que habías completado. Las fotos no se pueden guardar: si querés
                sumarlas, volvé a elegirlas en el paso 2.
              </Alert>
            ) : null}
            {step === 0 && isCatalogueLoading ? (
              <Typography color="text.secondary">Cargando los modelos que aceptamos...</Typography>
            ) : null}
            {step === 0 && !isCatalogueLoading && catalogueFailed ? (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => void loadPhoneModels()}>
                    Reintentar
                  </Button>
                }
              >
                No pudimos cargar los modelos que cotizamos. Probá de nuevo en un momento.
              </Alert>
            ) : null}
            {/*
              An empty catalogue used to fall back to free text. It cannot any more: a request
              names a catalogue entry, so with nothing to name there is no request to make.
              Saying so, with a way to reach PhoneTec, is the whole of what the page can offer.
            */}
            {step === 0 && !isCatalogueLoading && !catalogueFailed && !phoneModels.length ? (
              <Alert severity="info">
                Todavía no podemos cotizar online. Escribinos y te ayudamos con tu equipo.
              </Alert>
            ) : null}
            {step === 0 && !isCatalogueLoading && !catalogueFailed && phoneModels.length ? (
              <>
                {onlyBrand ? (
                  // Told, not asked. The applicant still sees which brand they are quoting.
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      MARCA
                    </Typography>
                    <Typography variant="body1" fontWeight={700}>
                      {onlyBrand}
                    </Typography>
                  </Box>
                ) : (
                  <FormControl fullWidth required>
                    <InputLabel id="catalogue-brand-label">Marca</InputLabel>
                    <Select
                      labelId="catalogue-brand-label"
                      label="Marca"
                      value={selectedBrand}
                      onChange={(e) => {
                        setSelectedBrand(e.target.value);
                        setSelectedModelId('');
                        setStorageGb('');
                      }}
                    >
                      {catalogueBrands.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <FormControl fullWidth required disabled={!brandName}>
                  <InputLabel id="catalogue-model-label">Modelo</InputLabel>
                  <Select
                    labelId="catalogue-model-label"
                    label="Modelo"
                    value={selectedModelId}
                    onChange={(e) => {
                      setSelectedModelId(e.target.value);
                      setStorageGb('');
                    }}
                  >
                    {modelsForBrand.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.model}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth required disabled={!selectedModel}>
                  <InputLabel id="catalogue-storage-label">Capacidad</InputLabel>
                  <Select
                    labelId="catalogue-storage-label"
                    label="Capacidad"
                    value={storageGb}
                    onChange={(e) => setStorageGb(e.target.value)}
                  >
                    {(selectedModel?.storageOptions || []).map((option) => (
                      <MenuItem key={option} value={String(option)}>
                        {option} GB
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Cotizamos los modelos de esta lista. Si el tuyo no está, escribinos y lo vemos con
                  vos.
                </Typography>
              </>
            ) : null}
            {step === 1 ? (
              <>
                {declaredFields.length === 0 ? (
                  <Alert severity="info">
                    Por ahora no necesitamos que declares nada más sobre el estado del equipo.
                    PhoneTec lo revisa en la inspección.
                  </Alert>
                ) : null}

                {/*
                  Rendered from the configuration, not from a list in this file. Adding a
                  question, or a fourth and fifth answer to one, is something PhoneTec does in
                  the panel: nothing here changes and nothing gets deployed.
                */}
                {declaredFields.map((field) =>
                  field.type === 'PERCENTAGE' ? (
                    <Box key={field.id}>
                      <TextField
                        fullWidth
                        required={field.isRequired}
                        type="number"
                        label={field.label}
                        value={answers[field.id] || ''}
                        onChange={(e) =>
                          setAnswers((current) => ({ ...current, [field.id]: e.target.value }))
                        }
                        placeholder="85"
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        helperText={
                          field.helpText || field.helpDetails ? (
                            <>
                              {field.helpText}{' '}
                              {field.helpDetails ? (
                                <Link
                                  component="button"
                                  type="button"
                                  variant="caption"
                                  underline="always"
                                  onClick={() =>
                                    setOpenHelpFieldId((current) =>
                                      current === field.id ? null : field.id,
                                    )
                                  }
                                  aria-expanded={openHelpFieldId === field.id}
                                >
                                  ¿Cómo lo veo en mi celular?
                                </Link>
                              ) : null}
                            </>
                          ) : null
                        }
                      />
                      {field.helpDetails ? (
                        <Collapse in={openHelpFieldId === field.id} unmountOnExit>
                          <Box
                            sx={{
                              bgcolor: 'background.default',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              p: 2,
                              mt: 1,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {field.helpDetails}
                            </Typography>
                          </Box>
                        </Collapse>
                      ) : null}
                    </Box>
                  ) : (
                    <FormControl key={field.id} fullWidth required={field.isRequired}>
                      <InputLabel id={`declared-field-${field.id}-label`}>{field.label}</InputLabel>
                      <Select
                        labelId={`declared-field-${field.id}-label`}
                        label={field.label}
                        value={answers[field.id] || ''}
                        onChange={(e) =>
                          setAnswers((current) => ({ ...current, [field.id]: e.target.value }))
                        }
                      >
                        {field.options.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {field.helpText ? (
                        <Typography variant="caption" color="text.secondary" mt={0.5}>
                          {field.helpText}
                        </Typography>
                      ) : null}
                    </FormControl>
                  ),
                )}
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Fotos del equipo (opcional)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Ayudan a PhoneTec a revisar mejor lo que declaraste. Hasta 5 fotos, en JPG, PNG
                    o WEBP, de menos de 5 MB cada una.
                  </Typography>
                  <PrimaryButton
                    component="label"
                    variant="outlined"
                    color="inherit"
                    sx={{ boxShadow: 'none' }}
                  >
                    Elegir fotos
                    <input
                      hidden
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      aria-label="Fotos del equipo"
                      onChange={(event) => handlePhotoSelection(event.target.files)}
                    />
                  </PrimaryButton>
                  {photos.length ? (
                    <Stack
                      component="ul"
                      spacing={0.5}
                      sx={{ listStyle: 'none', m: 0, p: 0, mt: 1.5 }}
                    >
                      {photos.map((photo, index) => (
                        <Stack
                          component="li"
                          key={`${photo.name}-${index}`}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body2">{photo.name}</Typography>
                          <Link
                            component="button"
                            type="button"
                            variant="caption"
                            onClick={() =>
                              setPhotos((current) => current.filter((_, i) => i !== index))
                            }
                          >
                            Quitar
                          </Link>
                        </Stack>
                      ))}
                    </Stack>
                  ) : null}
                  {photoWarning ? (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      {photoWarning}
                    </Alert>
                  ) : null}
                </Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Algo más para contarnos (opcional)"
                  value={declaredCondition}
                  onChange={(e) => setDeclaredCondition(e.target.value)}
                  placeholder="Ej.: se golpeó una vez, tiene una funda desde que lo compré..."
                  helperText="Cualquier detalle adicional que ayude a PhoneTec a evaluar tu equipo."
                />
              </>
            ) : null}
            {/*
              The contact is not asked for separately: it is the account's (ADR 0008). Without
              an account, this step registers or signs in, and the same button sends the request,
              so the details are typed once.
            */}
            {step === 2 && isBootstrapping ? (
              <Stack direction="row" spacing={2} alignItems="center" role="status">
                <CircularProgress size={24} color="secondary" />
                <Typography>Revisando tu sesión...</Typography>
              </Stack>
            ) : null}
            {step === 2 && !isBootstrapping && isCustomer && user ? (
              <Box
                sx={{
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography variant="overline" color="text.secondary">
                  COTIZÁS CON TU CUENTA
                </Typography>
                <Typography fontWeight={700}>{user.fullName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                  {user.phone ? ` · ${user.phone}` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  PhoneTec usa estos datos para coordinar la revisión. ¿No sos vos?{' '}
                  <Link component="button" type="button" variant="body2" onClick={logout}>
                    Salí
                  </Link>
                </Typography>
              </Box>
            ) : null}
            {step === 2 && !isBootstrapping && isAuthenticated && !isCustomer ? (
              <Alert
                severity="warning"
                action={
                  <Button color="inherit" size="small" onClick={logout}>
                    Salir
                  </Button>
                }
              >
                Ingresaste con una cuenta del panel de PhoneTec. Para cotizar como cliente, salí y
                ingresá con una cuenta de cliente.
              </Alert>
            ) : null}
            {step === 2 && !isBootstrapping && !isAuthenticated && activationSentTo ? (
              <Alert severity="info">
                Ya tenías una cuenta creada en el local de PhoneTec. Te enviamos un mail a{' '}
                {activationSentTo} para activarla y elegir tu contraseña. Cuando la actives, volvés
                acá con lo que completaste.
              </Alert>
            ) : null}
            {step === 2 && !isBootstrapping && !isAuthenticated && !activationSentTo ? (
              <Stack spacing={2}>
                <Typography color="text.secondary">
                  Para ver tu estimación necesitás una cuenta de PhoneTec. Creala o ingresá acá
                  mismo: con el mismo botón enviamos tu solicitud.
                </Typography>
                <Tabs
                  value={accountMode}
                  onChange={(_event, value: AccountMode) => {
                    setAccountMode(value);
                    setAccountErrors({});
                    setErrorMessage(null);
                  }}
                  variant="fullWidth"
                  aria-label="Cuenta para cotizar"
                >
                  <Tab value="register" label="Soy nuevo" />
                  <Tab value="login" label="Ya tengo cuenta" />
                </Tabs>
                {accountMode === 'register' ? (
                  <>
                    <TextField
                      label="Nombre y apellido"
                      autoComplete="name"
                      value={registration.fullName}
                      onChange={(e) => {
                        setRegistration((current) => ({ ...current, fullName: e.target.value }));
                        setAccountErrors((current) => ({ ...current, fullName: undefined }));
                      }}
                      error={Boolean(accountErrors.fullName)}
                      helperText={accountErrors.fullName}
                      fullWidth
                    />
                    <TextField
                      label="Email"
                      type="email"
                      autoComplete="email"
                      value={registration.email}
                      onChange={(e) => {
                        setRegistration((current) => ({ ...current, email: e.target.value }));
                        setAccountErrors((current) => ({ ...current, email: undefined }));
                      }}
                      error={Boolean(accountErrors.email)}
                      helperText={accountErrors.email || 'Te enviamos ahí la confirmación.'}
                      fullWidth
                    />
                    <TextField
                      label="Teléfono"
                      type="tel"
                      autoComplete="tel"
                      placeholder="099 123 456"
                      value={registration.phone}
                      onChange={(e) => {
                        setRegistration((current) => ({ ...current, phone: e.target.value }));
                        setAccountErrors((current) => ({ ...current, phone: undefined }));
                      }}
                      error={Boolean(accountErrors.phone)}
                      helperText={
                        accountErrors.phone || 'Lo usamos para coordinar la revisión del equipo.'
                      }
                      fullWidth
                    />
                    <TextField
                      label="Contraseña"
                      type="password"
                      autoComplete="new-password"
                      value={registration.password}
                      onChange={(e) => {
                        setRegistration((current) => ({ ...current, password: e.target.value }));
                        setAccountErrors((current) => ({ ...current, password: undefined }));
                      }}
                      error={Boolean(accountErrors.password)}
                      helperText={
                        accountErrors.password ||
                        'Mínimo 10 caracteres, con una mayúscula, un número y un carácter especial.'
                      }
                      fullWidth
                    />
                    <TextField
                      label="Repetir contraseña"
                      type="password"
                      autoComplete="new-password"
                      value={passwordConfirmation}
                      onChange={(e) => {
                        setPasswordConfirmation(e.target.value);
                        setConfirmationError(undefined);
                      }}
                      error={Boolean(confirmationError)}
                      helperText={confirmationError}
                      fullWidth
                    />
                    <Alert severity="info">
                      Usaremos tu nombre, email y teléfono únicamente para gestionar tu cuenta, tus
                      cotizaciones, reservas y compras, y para contactarte sobre ellas.
                    </Alert>
                  </>
                ) : (
                  <>
                    <TextField
                      label="Email"
                      type="email"
                      autoComplete="email"
                      value={loginValues.email}
                      onChange={(e) => {
                        setLoginValues((current) => ({ ...current, email: e.target.value }));
                        setAccountErrors((current) => ({ ...current, email: undefined }));
                      }}
                      error={Boolean(accountErrors.email)}
                      helperText={accountErrors.email}
                      fullWidth
                    />
                    <TextField
                      label="Contraseña"
                      type="password"
                      autoComplete="current-password"
                      value={loginValues.password}
                      onChange={(e) => {
                        setLoginValues((current) => ({ ...current, password: e.target.value }));
                        setAccountErrors((current) => ({ ...current, password: undefined }));
                      }}
                      error={Boolean(accountErrors.password)}
                      helperText={accountErrors.password}
                      fullWidth
                    />
                  </>
                )}
              </Stack>
            ) : null}
            <Stack direction="row" justifyContent="space-between" spacing={1.5} pt={1}>
              <PrimaryButton
                type="button"
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setErrorMessage(null);
                  setStep((current) => Math.max(0, current - 1));
                }}
                sx={{ visibility: step === 0 ? 'hidden' : 'visible', boxShadow: 'none' }}
              >
                Atrás
              </PrimaryButton>
              {step < 2 ? (
                // Without a catalogue there is nothing to advance to: a request has to name a
                // model, so the step cannot be completed.
                // Keyed apart from the send button below. Sharing one element, React would turn this
                // very button into a submit while its click is still being handled, and the browser
                // would send the request the moment the last step appeared (#107, found in the
                // browser).
                <PrimaryButton
                  key="next"
                  type="button"
                  onClick={advance}
                  disabled={step === 0 && !isCatalogueLoading && !phoneModels.length}
                >
                  Siguiente
                </PrimaryButton>
              ) : isBootstrapping || (isAuthenticated && !isCustomer) || activationSentTo ? null : (
                <PrimaryButton
                  key="send"
                  type="submit"
                  disabled={isSubmitting || !areFieldsLoaded}
                  // It appears where "Siguiente" was: the second click of a double click must not
                  // send a request nobody has looked at yet.
                  onClick={(event) => {
                    if (event.detail > 1) event.preventDefault();
                  }}
                >
                  {isSubmitting
                    ? 'Enviando...'
                    : isCustomer
                      ? 'Ver mi estimación'
                      : accountMode === 'register'
                        ? 'Crear cuenta y ver mi estimación'
                        : 'Ingresar y ver mi estimación'}
                </PrimaryButton>
              )}
            </Stack>
          </Stack>
        </SectionCard>
      )}
      <Typography variant="caption" color="text.secondary" textAlign="center">
        No es una oferta de compra vinculante. PhoneTec define el resultado final luego de la
        inspección física.
      </Typography>
    </NarrowPage>
  );
}
